import React from 'react';
import { Card, Tag, Tooltip, Space } from 'antd';
import { ClockCircleOutlined, UserOutlined, TeamOutlined, WarningOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import { Appointment } from '../../services/appointmentApi';
import './AppointmentCard.css';

interface AppointmentCardProps {
  appointment: Appointment;
  onClick: () => void;
}

export const AppointmentCard: React.FC<AppointmentCardProps> = ({ appointment, onClick }) => {
  // Determine color based on status
  const getStatusColor = (): string => {
    switch (appointment.status) {
      case 'scheduled':
        return '#1890ff'; // Blue
      case 'confirmed':
        return '#52c41a'; // Green
      case 'in_progress':
        return '#fa8c16'; // Orange
      case 'completed':
        return '#d9d9d9'; // Gray
      case 'cancelled':
        return '#ff4d4f'; // Red
      default:
        return '#1890ff';
    }
  };

  const getStatusLabel = (): string => {
    switch (appointment.status) {
      case 'scheduled':
        return 'Scheduled';
      case 'confirmed':
        return 'Confirmed';
      case 'in_progress':
        return 'In Progress';
      case 'completed':
        return 'Completed';
      case 'cancelled':
        return 'Cancelled';
      case 'no_show':
        return 'No Show';
      default:
        return appointment.status;
    }
  };

  const getTypeColor = (): string => {
    switch (appointment.appointment_type) {
      case 'diagnosis':
        return 'blue';
      case 'repair':
        return 'green';
      case 'inspection':
        return 'orange';
      case 'pickup':
        return 'purple';
      case 'delivery':
        return 'cyan';
      default:
        return 'default';
    }
  };

  // Check if multi-tech appointment
  const additionalTechs = appointment.technicians?.filter(t => !t.is_primary) || [];
  const hasMultipleTechs = additionalTechs.length > 0;

  // Format time
  const startTime = dayjs(appointment.start_time).format('h:mm A');
  const endTime = dayjs(appointment.end_time).format('h:mm A');

  return (
    <Card
      className="appointment-card"
      style={{
        borderLeft: `4px solid ${getStatusColor()}`,
        cursor: 'pointer'
      }}
      size="small"
      hoverable
      onClick={onClick}
      bodyStyle={{ padding: '8px' }}
    >
      <div className="appointment-card-content">
        {/* Time */}
        <div className="appointment-time">
          <ClockCircleOutlined />
          <span>{startTime} - {endTime}</span>
        </div>

        {/* Title */}
        <div className="appointment-title">
          {appointment.title}
        </div>

        {/* Customer */}
        {appointment.customer && (
          <div className="appointment-customer">
            <UserOutlined />
            <span>
              {appointment.customer.first_name} {appointment.customer.last_name}
            </span>
          </div>
        )}

        {/* Tags */}
        <Space size="small" wrap style={{ marginTop: '4px' }}>
          <Tag color={getTypeColor()} style={{ margin: 0 }}>
            {appointment.appointment_type.toUpperCase()}
          </Tag>

          {hasMultipleTechs && (
            <Tooltip title={`${additionalTechs.length} additional technician(s)`}>
              <Tag icon={<TeamOutlined />} color="purple" style={{ margin: 0 }}>
                +{additionalTechs.length}
              </Tag>
            </Tooltip>
          )}

          {appointment.has_conflict && !appointment.conflict_acknowledged && (
            <Tooltip title="Scheduling conflict">
              <Tag icon={<WarningOutlined />} color="red" style={{ margin: 0 }}>
                CONFLICT
              </Tag>
            </Tooltip>
          )}

          {appointment.waiting_on_parts && (
            <Tooltip title="Waiting on parts">
              <Tag color="orange" style={{ margin: 0 }}>
                PARTS
              </Tag>
            </Tooltip>
          )}
        </Space>
      </div>
    </Card>
  );
};
